/**
 * Serviço para gerenciar sessão no web/mobile.
 * - Web: localStorage
 * - App nativo (Capacitor): Preferences (com migração automática)
 */
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'

const ACCESS_TOKEN_KEY = 'imobdesk_access_token'
const JUST_LOGGED_IN_KEY = 'imobdesk_just_logged_in'
let accessTokenCache: string | null = null
let initialized = false
let initializePromise: Promise<void> | null = null

function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

function isSecureProdDomain(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  return host.endsWith('.imobdesk.com.br')
}

function canPersistWebTokenInLocalStorage(): boolean {
  return !isSecureProdDomain()
}

function getLocalStorageToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch (_error) {
    return null
  }
}

function setLocalStorageToken(token: string): void {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
  } catch (_error) {}
}

function removeLocalStorageToken(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  } catch (_error) {}
}

function getLocalStorageValue(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch (_error) {
    return null
  }
}

async function getNativeToken(): Promise<string | null> {
  try {
    const result = await Preferences.get({ key: ACCESS_TOKEN_KEY })
    return result.value ?? null
  } catch (_error) {
    return null
  }
}

async function setNativeToken(token: string): Promise<void> {
  try {
    await Preferences.set({ key: ACCESS_TOKEN_KEY, value: token })
  } catch (_error) {}
}

async function removeNativeToken(): Promise<void> {
  try {
    await Preferences.remove({ key: ACCESS_TOKEN_KEY })
  } catch (_error) {}
}

/**
 * Hidrata o cache de token e migra automaticamente localStorage -> Preferences no mobile.
 */
export async function initializeTokenStorage(): Promise<void> {
  if (initialized) return
  if (initializePromise) {
    await initializePromise
    return
  }

  initializePromise = (async () => {
    const localToken = canPersistWebTokenInLocalStorage()
      ? getLocalStorageToken()
      : null

    if (isNativeApp()) {
      const nativeToken = await getNativeToken()

      if (nativeToken) {
        accessTokenCache = nativeToken
        if (localToken) {
          removeLocalStorageToken()
        }
      } else if (localToken) {
        accessTokenCache = localToken
        await setNativeToken(localToken)
        removeLocalStorageToken()
      } else {
        accessTokenCache = null
      }
    } else {
      accessTokenCache = localToken
      if (!canPersistWebTokenInLocalStorage()) {
        removeLocalStorageToken()
      }
    }

    initialized = true
  })()

  try {
    await initializePromise
  } finally {
    initializePromise = null
  }
}

void initializeTokenStorage()

/**
 * Salva o access token no storage apropriado.
 */
export function saveAccessToken(token: string): void {
  accessTokenCache = token
  initialized = true

  if (isNativeApp()) {
    void setNativeToken(token)
    removeLocalStorageToken()
    return
  }

  if (canPersistWebTokenInLocalStorage()) {
    setLocalStorageToken(token)
  } else {
    removeLocalStorageToken()
  }
}

/**
 * Obtém o access token de forma síncrona (via cache em memória).
 * Use `getAccessTokenAsync` em fluxos críticos que rodam antes da hidratação inicial.
 */
export function getAccessToken(): string | null {
  if (accessTokenCache) {
    return accessTokenCache
  }

  if (canPersistWebTokenInLocalStorage()) {
    const localToken = getLocalStorageToken()
    if (localToken) {
      accessTokenCache = localToken
      return localToken
    }
  }

  return null
}

/**
 * Obtém o token garantindo hidratação do storage.
 */
export async function getAccessTokenAsync(): Promise<string | null> {
  await initializeTokenStorage()
  return getAccessToken()
}

/** Mantido por compatibilidade; apenas access token é persistido no frontend. */
export function saveTokens(accessToken: string, _refreshToken?: string): void {
  saveAccessToken(accessToken)
}

/**
 * Remove token do storage apropriado.
 */
export function clearTokens(): void {
  accessTokenCache = null
  initialized = true
  removeLocalStorageToken()

  if (isNativeApp()) {
    void removeNativeToken()
  }
}

/**
 * Lê a flag de "acabou de logar" usada para evitar efeitos duplicados de 401
 * no bootstrap da sessão.
 */
export function isJustLoggedIn(): boolean {
  const value = getLocalStorageValue(JUST_LOGGED_IN_KEY)
  return value === 'true'
}

/**
 * Verifica se existe um token de acesso salvo
 */
export function hasAccessToken(): boolean {
  return getAccessToken() !== null
}

/**
 * Chave para armazenar flag de usuário novo
 */
const IS_NEW_USER_KEY = 'imobdesk_is_new_user'

/**
 * Marca o usuário como novo (após cadastro)
 */
export function markUserAsNew(): void {
  try {
    localStorage.setItem(IS_NEW_USER_KEY, 'true')
  } catch (error) {
  }
}

/**
 * Verifica se o usuário é novo
 */
export function isNewUser(): boolean {
  try {
    return localStorage.getItem(IS_NEW_USER_KEY) === 'true'
  } catch (error) {
    return false
  }
}

/**
 * Remove a flag de usuário novo (após criar dashboard ou outra ação)
 */
export function clearNewUserFlag(): void {
  try {
    localStorage.removeItem(IS_NEW_USER_KEY)
  } catch (error) {
  }
}

